import React, { useState } from 'react';
import { User, Settings, Share2, Download, Award, Calendar, TrendingUp, Heart, Shield, Zap, Sparkles, Star } from 'lucide-react';

interface ProfileProps {
  sauceProfile: any;
}

export default function Profile({ sauceProfile }: ProfileProps) {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'DrizzID™ Overview', icon: User },
    { id: 'functional', label: 'Functional Profile', icon: Sparkles },
    { id: 'history', label: 'Enhancement History', icon: Calendar },
    { id: 'achievements', label: 'Wellness Achievements', icon: Award },
    { id: 'settings', label: 'AI Settings', icon: Settings }
  ];

  const achievements = [
    { 
      title: 'DrizzID™ Pioneer', 
      description: 'Completed your first enhanced DrizzID™ profile', 
      date: 'Today', 
      earned: true,
      category: 'beauty',
      icon: Heart
    },
    { 
      title: 'Functional Explorer', 
      description: 'Tried 5 beauty-infused recommendations', 
      date: 'Pending', 
      earned: false,
      category: 'beauty',
      icon: Heart
    },
    { 
      title: 'Performance Optimizer', 
      description: 'Completed 10 performance-enhanced meals', 
      date: 'Pending', 
      earned: false,
      category: 'performance',
      icon: Zap
    },
    { 
      title: 'Wellness Ambassador', 
      description: 'Shared your functional profile with 3 friends', 
      date: 'Pending', 
      earned: false,
      category: 'wellness',
      icon: Shield
    }
  ];

  const functionalIngredients = [
    { name: 'Marine Collagen', category: 'Beauty', benefit: 'Skin elasticity', compatibility: 95 },
    { name: 'Ashwagandha', category: 'Wellness', benefit: 'Stress reduction', compatibility: 88 },
    { name: 'L-Theanine', category: 'Performance', benefit: 'Focus enhancement', compatibility: 92 },
    { name: 'Hyaluronic Acid', category: 'Beauty', benefit: 'Hydration boost', compatibility: 90 },
    { name: 'Probiotics', category: 'Wellness', benefit: 'Gut health', compatibility: 85 },
    { name: 'Caffeine', category: 'Performance', benefit: 'Energy boost', compatibility: 87 }
  ];

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'beauty': return 'from-pink-500 to-rose-500';
      case 'wellness': return 'from-green-500 to-emerald-500';
      case 'performance': return 'from-blue-500 to-cyan-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'beauty': return Heart;
      case 'wellness': return Shield;
      case 'performance': return Zap;
      default: return Star;
    }
  };

  return (
    <div className="min-h-screen py-16 sm:py-20 relative overflow-hidden">
      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/20 via-purple-600/30 to-cyan-600/20 animate-pulse"></div>
      
      {/* AI Neural Network */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        {/* Enhanced Profile Header - Mobile Optimized */}
        <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-cyan-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
          <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6 lg:space-x-8">
            {/* Enhanced Avatar */}
            <div className="relative">
              <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                <User className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-xs font-bold px-2 sm:px-3 py-1 rounded-full">
                {sauceProfile.profileType}
              </div>
              <div className="absolute -top-1 -left-1 sm:-top-2 sm:-left-2 bg-cyan-500 text-white text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                AI Enhanced
              </div>
            </div>

            {/* Enhanced Profile Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Your Enhanced DrizzID™ Profile</h1>
              <p className="text-gray-400 mb-3 sm:mb-4 text-sm sm:text-base">
                Created today • Profile Type: {sauceProfile.profileType} • Primary Goal: {sauceProfile.primaryGoal || 'Balance'}
              </p>
              <div className="flex flex-col sm:flex-row justify-center md:justify-start gap-3 sm:gap-4">
                <button className="bg-gradient-to-r from-cyan-500 to-purple-600 text-white px-4 sm:px-6 py-2 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 flex items-center justify-center space-x-2 text-sm sm:text-base">
                  <Share2 className="h-4 w-4" />
                  <span>Share DrizzID™</span>
                </button>
                <button className="border border-cyan-500/50 text-cyan-400 px-4 sm:px-6 py-2 rounded-full font-semibold hover:bg-cyan-500/10 transition-all duration-300 flex items-center justify-center space-x-2 text-sm sm:text-base">
                  <Download className="h-4 w-4" />
                  <span>Export Data</span>
                </button>
              </div>
            </div>

            {/* Enhanced Quick Stats */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
              <div className="bg-black/50 rounded-lg p-3 sm:p-4">
                <div className="text-lg sm:text-2xl font-bold text-cyan-400">{sauceProfile.beautyScore || 85}%</div>
                <div className="text-gray-400 text-xs sm:text-sm">Beauty</div>
              </div>
              <div className="bg-black/50 rounded-lg p-3 sm:p-4">
                <div className="text-lg sm:text-2xl font-bold text-green-400">{sauceProfile.wellnessScore || 78}%</div>
                <div className="text-gray-400 text-xs sm:text-sm">Wellness</div>
              </div>
              <div className="bg-black/50 rounded-lg p-3 sm:p-4">
                <div className="text-lg sm:text-2xl font-bold text-blue-400">{sauceProfile.performanceScore || 92}%</div>
                <div className="text-gray-400 text-xs sm:text-sm">Performance</div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Tab Navigation - Mobile Optimized */}
        <div className="mb-6 sm:mb-8">
          {/* Mobile Tab Selector */}
          <div className="sm:hidden bg-gray-900/50 rounded-xl p-2 mb-4">
            <select 
              value={activeTab} 
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full bg-transparent text-white p-2 rounded-lg border border-gray-700 focus:border-cyan-500 focus:outline-none"
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id} className="bg-gray-900">
                  {tab.label}
                </option>
              ))}
            </select>
          </div>

          {/* Desktop Tab Navigation */}
          <div className="hidden sm:flex flex-wrap justify-center bg-gray-900/50 rounded-xl p-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-3 lg:px-6 py-2 lg:py-3 rounded-lg font-medium transition-all duration-300 text-sm lg:text-base ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden lg:inline">{tab.label}</span>
                <span className="lg:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Enhanced Tab Content - Mobile Optimized */}
        <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-cyan-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8">
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center space-x-2 sm:space-x-3">
                <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-400" />
                <span>Enhanced DrizzID™ Overview</span>
              </h2>
              
              {/* Enhanced Taste Metrics - Mobile Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
                {[
                  { label: 'Intensity', value: sauceProfile.intensity, color: 'from-red-500 to-orange-500', benefit: 'Bioavailability' },
                  { label: 'Heat Level', value: sauceProfile.heat, color: 'from-orange-500 to-yellow-500', benefit: 'Metabolism' },
                  { label: 'Complexity', value: sauceProfile.complexity, color: 'from-purple-500 to-pink-500', benefit: 'Synergy' },
                  { label: 'Adventure', value: sauceProfile.adventure, color: 'from-blue-500 to-purple-500', benefit: 'Innovation' }
                ].map((metric, index) => (
                  <div key={index} className="bg-black/50 rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 text-center">
                    <div className={`w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full bg-gradient-to-r ${metric.color} mx-auto mb-2 sm:mb-4 flex items-center justify-center`}>
                      <span className="text-white font-bold text-sm sm:text-lg lg:text-xl">{metric.value}%</span>
                    </div>
                    <h3 className="text-white font-semibold text-sm sm:text-base">{metric.label}</h3>
                    <p className="text-cyan-300 text-xs">{metric.benefit}</p>
                  </div>
                ))}
              </div>

              {/* Enhanced Flavor Profile Chart */}
              <div className="bg-black/50 rounded-lg sm:rounded-xl p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-400" />
                  <span>AI-Enhanced Flavor Profile Analysis</span>
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  {['Sweet', 'Salty', 'Sour', 'Bitter', 'Umami', 'Spicy'].map((flavor, index) => {
                    const value = Math.floor(Math.random() * 100) + 1;
                    return (
                      <div key={index} className="flex items-center space-x-3 sm:space-x-4">
                        <span className="text-gray-400 w-12 sm:w-16 text-xs sm:text-sm">{flavor}</span>
                        <div className="flex-1 bg-gray-800 rounded-full h-1.5 sm:h-2">
                          <div 
                            className="bg-gradient-to-r from-cyan-500 to-purple-600 h-1.5 sm:h-2 rounded-full transition-all duration-1000"
                            style={{ width: `${value}%` }}
                          ></div>
                        </div>
                        <span className="text-white text-xs sm:text-sm w-8 sm:w-12">{value}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'functional' && (
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center space-x-2 sm:space-x-3">
                <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-400" />
                <span>Functional Ingredient Profile</span>
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                {functionalIngredients.map((ingredient, index) => {
                  const CategoryIcon = getCategoryIcon(ingredient.category);
                  return (
                    <div key={index} className="bg-black/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-gray-800">
                      <div className="flex items-start justify-between mb-3 sm:mb-4">
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r ${getCategoryColor(ingredient.category)} rounded-lg flex items-center justify-center`}>
                            <CategoryIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold text-sm sm:text-base">{ingredient.name}</h4>
                            <p className="text-gray-400 text-xs sm:text-sm">{ingredient.category}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-cyan-400 font-bold text-sm sm:text-base">{ingredient.compatibility}%</div>
                          <div className="text-gray-400 text-xs">Match</div>
                        </div>
                      </div>
                      <p className="text-gray-300 text-xs sm:text-sm mb-2 sm:mb-3">{ingredient.benefit}</p>
                      <div className="w-full bg-gray-800 rounded-full h-1.5 sm:h-2">
                        <div 
                          className={`bg-gradient-to-r ${getCategoryColor(ingredient.category)} h-1.5 sm:h-2 rounded-full`}
                          style={{ width: `${ingredient.compatibility}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-black/50 rounded-lg sm:rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">AI Recommendations</h3>
                <div className="space-y-2 sm:space-y-3">
                  {(sauceProfile.functionalRecommendations || ['Marine Collagen', 'Ashwagandha', 'L-Theanine']).map((rec, index) => (
                    <div key={index} className="flex items-center space-x-2 sm:space-x-3 bg-gray-800/50 rounded-lg p-2 sm:p-3">
                      <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-cyan-400" />
                      <span className="text-white text-sm sm:text-base">{rec}</span>
                      <span className="text-cyan-400 text-xs sm:text-sm ml-auto">Recommended</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'achievements' && (
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Wellness Achievements</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {achievements.map((achievement, index) => {
                  const AchievementIcon = achievement.icon;
                  return (
                    <div key={index} className={`bg-black/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border ${
                      achievement.earned ? `border-${achievement.category === 'beauty' ? 'pink' : achievement.category === 'wellness' ? 'green' : 'blue'}-500/50` : 'border-gray-700'
                    }`}>
                      <div className="flex items-start space-x-3 sm:space-x-4">
                        <div className={`p-2 sm:p-3 rounded-full ${
                          achievement.earned 
                            ? `bg-gradient-to-r ${getCategoryColor(achievement.category)}` 
                            : 'bg-gray-700'
                        }`}>
                          <AchievementIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className={`font-bold mb-1 text-sm sm:text-base ${
                            achievement.earned ? 'text-white' : 'text-gray-400'
                          }`}>
                            {achievement.title}
                          </h3>
                          <p className="text-gray-400 text-xs sm:text-sm mb-2">{achievement.description}</p>
                          <span className={`text-xs px-2 py-1 rounded ${
                            achievement.earned 
                              ? `bg-${achievement.category === 'beauty' ? 'pink' : achievement.category === 'wellness' ? 'green' : 'blue'}-500/20 text-${achievement.category === 'beauty' ? 'pink' : achievement.category === 'wellness' ? 'green' : 'blue'}-400` 
                              : 'bg-gray-700 text-gray-400'
                          }`}>
                            {achievement.date}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Enhancement History</h2>
              <div className="text-center py-8 sm:py-12">
                <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-gray-600 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-400 mb-2">No Enhancement History Yet</h3>
                <p className="text-gray-500 text-sm sm:text-base">Start exploring functional recommendations to build your enhancement history</p>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">AI Settings</h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-black/50 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">AI Preferences</h3>
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 text-sm sm:text-base">AI-powered recommendations</span>
                      <button className="bg-cyan-500 w-10 h-5 sm:w-12 sm:h-6 rounded-full p-1 transition-colors">
                        <div className="bg-white w-3 h-3 sm:w-4 sm:h-4 rounded-full translate-x-5 sm:translate-x-6 transition-transform"></div>
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 text-sm sm:text-base">Functional ingredient alerts</span>
                      <button className="bg-cyan-500 w-10 h-5 sm:w-12 sm:h-6 rounded-full p-1 transition-colors">
                        <div className="bg-white w-3 h-3 sm:w-4 sm:h-4 rounded-full translate-x-5 sm:translate-x-6 transition-transform"></div>
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 text-sm sm:text-base">Weekly DrizzID™ updates</span>
                      <button className="bg-cyan-500 w-10 h-5 sm:w-12 sm:h-6 rounded-full p-1 transition-colors">
                        <div className="bg-white w-3 h-3 sm:w-4 sm:h-4 rounded-full translate-x-5 sm:translate-x-6 transition-transform"></div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}